import React, { Dispatch, useCallback } from 'react'

import cx from 'classnames'
import InvertedToggleBlock from 'conversations/components/InvertedToggleBlock'
import KeyboardTag from 'conversations/components/KeyboardTag'
import { getPreference } from 'conversations/modules/user/selectors'
import { hasOnlyMyConvosAccess } from 'conversations/utils/sectionUtils'
import { useDispatch, useSelector } from 'react-redux'
import { showSidebarPreferencesModal } from 'shared/store/actions/modal'
import { togglePreference, updateUserPreferenceAsync } from 'shared/store/actions/user'

import { ReactComponent as Arrow } from '@driftt/conversation-components/icons/arrow.svg'

import { RootAction } from 'typesafe-actions'

import styles from './styles.module.css'
import popoverStyles from 'conversations/components/NewChatSidebar/PopoverMenu/styles.module.css'
import utilityStyles from 'conversations/css/utility.module.css'

const SelectMenuItem: React.FC<{
  children?: React.ReactNode
  name: string
  onClick?: () => void
  'data-ocweb-id'?: string
}> = ({ children, name, onClick, 'data-ocweb-id': dataTestId }) => {
  return (
    <div className={styles.selectMenuItem} onClick={onClick} data-ocweb-id={dataTestId}>
      {name}
      {children && (
        <>
          <Arrow width="8" height="10" className={utilityStyles.rotate270} />
          {children}
        </>
      )}
    </div>
  )
}

const QuickReplyPreference: React.FC = () => {
  const showQuickReplies = useSelector(getPreference('showQuickReplies')) as boolean
  const dispatch = useDispatch<Dispatch<RootAction>>()
  const onToggle = useCallback(
    (e) => {
      e.stopPropagation()
      dispatch(
        updateUserPreferenceAsync.request({
          preference: 'showQuickReplies',
          value: !showQuickReplies,
        })
      )
    },
    [dispatch, showQuickReplies]
  )

  return (
    <InvertedToggleBlock
      data-ocweb-id="ToggleQuickReplies"
      title={'Show quick replies'}
      description={'See message suggestions above the composer'}
      checked={!!showQuickReplies}
      onToggle={onToggle}
    />
  )
}

const SendButtonPreference: React.FC = () => {
  const useSendButton = useSelector(getPreference('useSendButton'))
  const dispatch = useDispatch<Dispatch<RootAction>>()
  const onToggle = useCallback(
    (e) => {
      e.stopPropagation()
      dispatch(togglePreference('useSendButton'))
    },
    [dispatch]
  )

  return (
    <InvertedToggleBlock
      data-ocweb-id="ToggleSendButton"
      title={'Show send button'}
      description={
        <>
          Press <KeyboardTag value="Enter" /> to add a new line
        </>
      }
      checked={!!useSendButton}
      onToggle={onToggle}
    />
  )
}

export const PreferencesMenu: React.FC<{
  setShowPreferences: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ setShowPreferences }) => {
  const hasOnlyMyConvos = hasOnlyMyConvosAccess()
  const dispatch = useDispatch()

  return (
    <div className={popoverStyles.menu}>
      <SelectMenuItem name="Chat preferences" data-ocweb-id="ComposerPreferences">
        <div className={cx(styles.composerPreferences, styles.secondarySelectMenu)}>
          <SendButtonPreference />
          <QuickReplyPreference />
        </div>
      </SelectMenuItem>
      {!hasOnlyMyConvos && (
        <>
          <div className={styles.divider} />
          <SelectMenuItem
            data-ocweb-id="SidebarPreferences"
            name="Sidebar preferences"
            onClick={() => {
              dispatch(showSidebarPreferencesModal())
              setShowPreferences(false)
            }}
          />
        </>
      )}
    </div>
  )
}
